/**
 * Blatt „Neuer Absender" / „Absender ändern" (`sender-sheet`, Spec 24c,
 * Oberfläche C) — dieselbe Bauform wie `document-sheet` (`DocumentSheet`),
 * ohne Datei: `sender === null` heißt Anlegen.
 *
 * Öffnet aus zwei Stellen: aus `SenderField` heraus (`document-sender-create`,
 * vorbelegt mit dem Suchtext als Name, `onCreated` wählt den neuen Absender)
 * und aus `SenderList` heraus (Zeile klicken, Ändern-Modus mit `If-Match`).
 * Beide liegen in `features/documents/`, deshalb hier direkt eingebunden —
 * anders als `TenantCreateSheet`/`ContractCreateSheet` (verschiedene
 * Features) braucht es keinen Umweg über einen `onRequestNew`-Callback nach
 * außen.
 */

import { useRef, useState } from 'react'
import type { SubmitEvent } from 'react'

import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import type { EntitySelection } from '@/components/EntityPicker'
import { EntityPicker } from '@/components/EntityPicker'
import { EnumSelectField } from '@/components/EnumSelectField'
import { FIELD_CLASS, FormField, FormTextareaField } from '@/components/FormField'
import type { FieldError } from '@/components/FormField'
import { ApiError } from '@/lib/api'
import { useCreateSender, useDocumentCategories, useUpdateSender } from '@/features/documents/api'
import type { SenderRow } from '@/features/documents/api'
import { DOCUMENT_SUBJECT_KINDS } from '@/features/documents/DocumentSubjectField'
import { senderSubjectLabel } from '@/features/documents/labels'
import { EMPTY_SENDER_FORM, buildSenderCreateBody, buildSenderPatchBody } from '@/features/documents/sender-form'
import type { SenderFormValues } from '@/features/documents/sender-form'

interface SenderSheetProps {
  readonly open: boolean
  readonly onOpenChange: (open: boolean) => void
  /** `null` für Anlegen, sonst der zu ändernde Absender. */
  readonly sender: SenderRow | null
  /** Nur beim Anlegen aus dem Picker heraus: Vorbelegung von `sender-name`
   * mit dem bisherigen Suchtext. */
  readonly initialName?: string
  /** Ruft nach erfolgreichem Anlegen mit der Zeile — `SenderField` wählt sie
   * (Spec 24c, Oberfläche C: „Absender ist gewählt"). */
  readonly onCreated?: (sender: SenderRow) => void
}

function deriveForm(sender: SenderRow | null, initialName: string): SenderFormValues {
  if (sender === null) return { ...EMPTY_SENDER_FORM, name: initialName }
  return {
    name: sender.name,
    category: sender.default_category?.value ?? '',
    aliases: sender.aliases.join('\n'),
    ibans: sender.ibans.map((iban) => iban.display).join('\n'),
    vatId: sender.vat_id?.value ?? '',
    emailDomains: sender.email_domains.map((domain) => domain.value).join('\n'),
  }
}

function deriveSubjectSelection(sender: SenderRow | null): EntitySelection | null {
  if (sender?.default_subject == null) return null
  return { id: sender.default_subject.id, label: senderSubjectLabel(sender.default_subject) }
}

/** Der Server bildet ein Merkmal mit Index (`ibans[2]`) auf `sender-error-
 * ibans` ab (Spec 24c, Oberfläche C) — derselbe Fehlertext, ein Feldname
 * ohne Index für die Zuordnung zum Formularfeld. */
function stripIndex(field: string | null): string | null {
  return field?.replace(/\[\d+\]$/, '') ?? null
}

export function SenderSheet({ open, onOpenChange, sender, initialName = '', onCreated }: SenderSheetProps) {
  const isEdit = sender !== null
  const nameRef = useRef<HTMLInputElement>(null)

  const categories = useDocumentCategories()
  const createMutation = useCreateSender()
  const updateMutation = useUpdateSender()
  const isPending = createMutation.isPending || updateMutation.isPending

  const [form, setForm] = useState<SenderFormValues>(EMPTY_SENDER_FORM)
  const [initialForm, setInitialForm] = useState<SenderFormValues>(EMPTY_SENDER_FORM)
  const [subjectSelection, setSubjectSelection] = useState<EntitySelection | null>(null)
  const [initialSubjectId, setInitialSubjectId] = useState<string | null>(null)
  const [isMoreOpen, setIsMoreOpen] = useState(false)
  const [fieldError, setFieldError] = useState<FieldError | undefined>(undefined)
  const [generalError, setGeneralError] = useState<string | undefined>(undefined)

  // Jedes Öffnen beginnt frisch — dasselbe React-Muster wie `DocumentSheet`
  // („Adjusting state when a prop changes").
  const [wasOpen, setWasOpen] = useState(open)
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) {
      const nextForm = deriveForm(sender, initialName)
      setForm(nextForm)
      setInitialForm(nextForm)
      const selection = deriveSubjectSelection(sender)
      setSubjectSelection(selection)
      setInitialSubjectId(selection?.id ?? null)
      setIsMoreOpen(
        sender !== null &&
          (sender.aliases.length > 0 || sender.ibans.length > 0 || sender.vat_id !== null || sender.email_domains.length > 0),
      )
      setFieldError(undefined)
      setGeneralError(undefined)
    }
  }

  const subjectId = subjectSelection?.id ?? null
  const isDirty = JSON.stringify(form) !== JSON.stringify(initialForm) || subjectId !== initialSubjectId
  const hasName = form.name.trim() !== ''
  const canSubmit = hasName && (isEdit ? isDirty : true)

  function updateField(name: keyof SenderFormValues) {
    return (value: string) => {
      setForm((current) => ({ ...current, [name]: value }))
    }
  }

  async function submit() {
    if (!canSubmit || isPending) return
    setFieldError(undefined)
    setGeneralError(undefined)

    try {
      if (isEdit) {
        const body = buildSenderPatchBody(form, initialForm, subjectId, initialSubjectId)
        if (Object.keys(body).length > 0) {
          await updateMutation.mutateAsync({ senderId: sender.id, body, ifMatch: String(sender.version) })
        }
        onOpenChange(false)
      } else {
        const created = await createMutation.mutateAsync({ body: buildSenderCreateBody(form, subjectId) })
        onCreated?.(created)
        onOpenChange(false)
      }
    } catch (error) {
      if (error instanceof ApiError && error.field !== null) {
        setFieldError({ field: stripIndex(error.field) ?? error.field, message: error.message })
      } else if (error instanceof ApiError) {
        setGeneralError(error.message)
      } else {
        setGeneralError('Die Verbindung zum Server ist unterbrochen.')
      }
    }
  }

  async function handleSubmit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault()
    await submit()
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        showCloseButton={false}
        onOpenAutoFocus={(event) => {
          event.preventDefault()
          nameRef.current?.focus()
        }}
        className="data-[side=right]:!w-full data-[side=right]:sm:!max-w-none md:data-[side=right]:!w-[480px] md:data-[side=right]:!max-w-[480px]"
      >
        <form data-testid="sender-sheet" onSubmit={(event) => void handleSubmit(event)} className="flex h-full flex-col">
          <SheetHeader>
            <SheetTitle className="text-section font-(--font-weight-section) text-text">
              {isEdit ? 'Absender ändern' : 'Neuer Absender'}
            </SheetTitle>
          </SheetHeader>

          <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-4">
            <FormField
              ref={nameRef}
              label="Name"
              testId="sender-name"
              apiField="name"
              errorPrefix="sender"
              required
              value={form.name}
              onChange={updateField('name')}
              error={fieldError}
            />

            <EnumSelectField
              label="Standard-Art"
              testId="sender-category"
              apiField="default_category"
              errorPrefix="sender"
              options={categories.data ?? []}
              isPending={categories.isPending}
              value={form.category}
              onChange={updateField('category')}
              error={fieldError}
              placeholder="Ohne Art"
            />

            <EntityPicker
              testId="sender-subject-picker"
              label="Standard-Objekt"
              apiField="default_subject_id"
              errorPrefix="sender"
              value={subjectSelection}
              onSelect={setSubjectSelection}
              error={fieldError}
              searchable
              kinds={DOCUMENT_SUBJECT_KINDS}
              searchPlaceholder="Objekt oder Vertrag suchen"
              emptySearchTitle="Nichts gefunden."
              allowClear
              onClear={() => {
                setSubjectSelection(null)
              }}
              placeholder="Kein Objekt"
            />

            <details
              open={isMoreOpen}
              onToggle={(event) => {
                setIsMoreOpen(event.currentTarget.open)
              }}
            >
              <summary
                data-testid="sender-more"
                className="text-label text-muted min-h-(--tap-min) cursor-pointer list-item py-2"
              >
                Weitere Angaben
              </summary>

              <div className="flex flex-col gap-4 pt-2">
                <FormTextareaField
                  label="Weitere Namen · ein Name je Zeile"
                  testId="sender-aliases"
                  apiField="aliases"
                  errorPrefix="sender"
                  value={form.aliases}
                  onChange={updateField('aliases')}
                  error={fieldError}
                />

                <FormTextareaField
                  label="IBAN · eine je Zeile"
                  testId="sender-ibans"
                  apiField="ibans"
                  errorPrefix="sender"
                  value={form.ibans}
                  onChange={updateField('ibans')}
                  error={fieldError}
                />
                <LearnedLine values={sender?.ibans.filter((iban) => iban.learned).map((iban) => iban.display)} />

                <FormField
                  label="USt-ID"
                  testId="sender-vat-id"
                  apiField="vat_id"
                  errorPrefix="sender"
                  value={form.vatId}
                  onChange={updateField('vatId')}
                  error={fieldError}
                />

                <FormTextareaField
                  label="Mail-Domains · eine je Zeile"
                  testId="sender-domains"
                  apiField="email_domains"
                  errorPrefix="sender"
                  value={form.emailDomains}
                  onChange={updateField('emailDomains')}
                  error={fieldError}
                />
                <LearnedLine values={sender?.email_domains.filter((domain) => domain.learned).map((domain) => domain.value)} />
              </div>
            </details>
          </div>

          <SheetFooter>
            {generalError !== undefined && (
              <p data-testid="sender-error" role="alert" className="text-label text-status-overdue">
                {generalError}
              </p>
            )}
            <div className="ml-auto flex flex-wrap justify-end gap-2">
              <button
                type="button"
                data-testid="sender-cancel"
                onClick={() => {
                  onOpenChange(false)
                }}
                className={`text-text px-4 ${FIELD_CLASS}`}
              >
                Abbrechen
              </button>
              <button
                type="submit"
                data-testid="sender-save"
                disabled={!canSubmit || isPending}
                aria-busy={isPending}
                className="bg-accent rounded-card text-body px-4 text-accent-on transition-opacity duration-(--duration-fast) ease-(--ease-out) disabled:opacity-60"
              >
                {isEdit ? 'Speichern' : 'Anlegen'}
              </button>
            </div>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  )
}

/** Spec 24c, Oberfläche C: gelernte Merkmale tragen „erkannt" — nur Anzeige
 * von `learned`, kein Fachwert. Die Textarea kann das nicht je Zeile zeigen,
 * deshalb eine Zeile darunter. */
function LearnedLine({ values }: { values: string[] | undefined }) {
  if (!values || values.length === 0) return null
  return <p className="text-label text-muted -mt-2">erkannt: {values.join(' · ')}</p>
}
